import { z } from 'zod';
import { cardFactsSchema, PACES } from './card-facts';
import { dynamicPricingSchema } from './dynamic-pricing';
import { category, categoryValues, subcategoryValues } from './enums';
import { languageCodeSchema } from './facility';

// Localized text entry, mirroring the shape used by facility.title /
// productInProducts.description (jsonb arrays of { languageCode, text }).
export const productLocalizedTextSchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string().min(1).max(10_000),
});

export type ProductLocalizedText = z.infer<typeof productLocalizedTextSchema>;

// Star rating: numeric(2,1) on the DB side. Accept 1.0 - 5.0 in 0.5 steps.
export const productStarsSchema = z
  .number()
  .min(1)
  .max(5)
  .refine((v) => Number.isFinite(v) && (v * 2) % 1 === 0, {
    message: 'stars must be a multiple of 0.5 between 1 and 5',
  });

// Patch shape for productsService.update — extensible: add more keys here as
// new fields become updateable. Each key is independently optional; service
// applies whichever ones are present.
export const updateProductPatchSchema = z
  .object({
    description: productLocalizedTextSchema.optional(),
    stars: productStarsSchema.optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: 'patch must contain at least one field to update',
  });

export type UpdateProductPatch = z.infer<typeof updateProductPatchSchema>;

export const updateProductInput = z.object({
  id: z.uuid(),
  patch: updateProductPatchSchema,
});

export type UpdateProductInput = z.infer<typeof updateProductInput>;

// Categories whose budget-value tier filter applies. Legacy
// EXPERIENCE_CATEGORIES (Activity, Attraction, Dining) — non-experience rows
// are excluded from value-tier matching even if their budget columns are set.
export const experienceCategoryValues = [
  category.Activity,
  category.Attraction,
  category.Dining,
] as const;
export type ExperienceCategory = (typeof experienceCategoryValues)[number];

// VAT setting summary surfaced alongside a product (legacy findOne joins
// `product.costVat` and `product.priceVat`). The full vat_tax_setting row
// (sans org/createdOn/modifiedOn/deletedOn) is fine — the detail page only
// reads `title` and `percent`.
const vatTaxSettingSummarySchema = z
  .object({
    id: z.uuid(),
    title: z.string().nullable(),
    percent: z.string().nullable(),
  })
  .nullable();
export type VatTaxSettingSummary = z.infer<typeof vatTaxSettingSummarySchema>;

// Facility joined-detail shape — legacy withFacility() returns
// `{ id, title }[]` where `title` is the facility's jsonb localized array.
const facilityDetailSchema = z
  .array(
    z.object({
      id: z.uuid(),
      title: z.unknown(),
      /** Pool, garden, terrace…: listed apart as the hotel's outdoors. */
      isOutdoor: z.boolean().optional(),
      /** The facility's own mark, before this product's choice. */
      isOutdoorDefault: z.boolean().optional(),
    })
  )
  .nullable();

// Output shape — what router.byId / router.update return to the client.
// Mirrors legacy ProductController.findOne (product.service.ts:879) plus
// joined VAT settings and facility details. `hashtags` is a separate join
// table in legacy and is NOT part of findOne's payload.
export const updatedProductSchema = z.object({
  id: z.uuid(),
  title: z.unknown(),
  titleEn: z.string().nullable(),
  description: z.unknown(),
  stars: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  thumbnail: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  price: z.unknown(),
  currency: z.string().nullable(),
  suppliers: z.unknown(),
  catalogId: z.uuid(),
  catalogTitle: z.string(),
  isHidden: z.boolean(),
  isHiddenOnCms: z.boolean().nullable(),
  isHiddenOnMiniPlanner: z.boolean(),
  // Legacy-parity additions — read-only surface; the update patch above
  // intentionally does not expose these.
  attachments: z.unknown(),
  videos: z.unknown(),
  // Localized marketing sections (`[{ languageCode, sections: [{ title,
  // text }] }]`). Read-only; the Mini Planner's expanded card reads it.
  marketingDescription: z.unknown().optional(),
  // Curated source URLs (jsonb `string[]`). Read-only here; written via the
  // widened patch (`curatedUrls` in productWritableFields). Stays `unknown`
  // so legacy rows with a drifted shape still load; optional since the column
  // is nullable and predates older rows.
  curatedUrls: z.unknown().optional(),
  // Facility details — array of `{ id, title }`, optionally filtered by
  // the `languages` input. The raw jsonb UUID array stays in the DB row,
  // but the byId payload returns the joined facility objects (mirrors
  // legacy withFacility helper).
  facilities: facilityDetailSchema,
  tags: z.unknown(),
  legs: z.unknown(),
  parentId: z.uuid().nullable(),
  accountId: z.uuid().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  pricingType: z.string().nullable(),
  isGross: z.boolean().nullable(),
  costVatId: z.uuid().nullable(),
  priceVatId: z.uuid().nullable(),
  // Joined VAT summaries. Legacy product.controller.ts:findOne does
  // `leftJoinAndSelect('product.costVat')` + `priceVat`. `price` itself is
  // the stored value — legacy `recalculatePriceService.recalculatePrice` is
  // owned by the pricing track and intentionally NOT ported here.
  costVat: vatTaxSettingSummarySchema,
  priceVat: vatTaxSettingSummarySchema,
  // Raw jsonb — typed shape on write is `groupPriceThresholdSchema[]` but the
  // read side stays `unknown` so legacy rows with a drifted shape still load.
  // FE parses with groupPriceThresholdSchema.array().safeParse(...).
  groupPriceThresholds: z.unknown(),
  // XT-951 — dynamic pricing. Raw jsonb on read (parse with
  // `dynamicPricingSchema`); the flag picks it over the static `price`.
  isDynamicPricing: z.boolean(),
  dynamicPricing: z.unknown(),
  notes: z.string().nullable(),
  privateNotes: z.string().nullable(),
  // For travel agents, never travellers; the portal's planner card shows it.
  advisorNotes: z.string().nullable().optional(),
  // The planner card's facts; null when none are set.
  cardFacts: cardFactsSchema.nullable().optional(),
  // Budget tier (1..4 == $..$$$$). `budgetValue` is the manual override;
  // `defaultBudgetValue` is the computed tier. Consumers read
  // `budgetValue ?? defaultBudgetValue` for display; the supplier edit tab
  // writes `budgetValue`.
  budgetValue: z.number().int().min(1).max(4).nullable(),
  defaultBudgetValue: z.number().int().min(1).max(4).nullable(),
  // Free-form external reference (e.g. supplier-side product id). Read-only
  // here; the supplier edit tab writes it via the widened patch.
  externalProductId: z.string().nullable(),
  // Raw product `details` jsonb bag. Read-only here; the update service merges
  // virtual fields (e.g. `hideTitle`) into it without clobbering siblings.
  details: z.unknown().nullable(),
  // Projected from `details.hideTitleInItinerary` — when true the product's
  // title is hidden downstream (itinerary + preview). Written via the widened
  // patch's `hideTitle` field (merged into the details jsonb server-side).
  hideTitle: z.boolean(),
});

export type UpdatedProduct = z.infer<typeof updatedProductSchema>;

// Input for byId — accepts an optional `languages` filter. When provided,
// only facility rows with at least one title entry in one of the requested
// languages are returned. Mirrors legacy FindOneProductDto.
export const findOneProductInputSchema = z.object({
  id: z.uuid(),
  languages: z.array(languageCodeSchema).optional(),
});
export type FindOneProductInput = z.infer<typeof findOneProductInputSchema>;

// ---------------------------------------------------------------------------
// products.list — input + output. Mirrors the legacy `/products/list` payload
// so every legacy filter is honoured at the wire shape. Service behaviour
// for each filter lives in `apps/products/api/src/products/service.ts`.
// ---------------------------------------------------------------------------

const categoryEnumValues = categoryValues as unknown as readonly [
  string,
  ...string[],
];
const subcategoryEnumValues = subcategoryValues as unknown as readonly [
  string,
  ...string[],
];

// 4-corner axis-aligned bbox. When combined with northWest/southEast fields
// on the input, the service prefers the 8-point polygon (legacy parity).
export const productBboxSchema = z.object({
  northEastLat: z.number(),
  northEastLng: z.number(),
  southWestLat: z.number(),
  southWestLng: z.number(),
});

export type ProductBbox = z.infer<typeof productBboxSchema>;

export const productSortFields = [
  'title',
  'price',
  'modifiedOn',
  'stars',
  // Mini Planner: the price a card shows (a hotel with none: its average sold
  // price per room night), room nights sold, the distance to the chapter's
  // hotel (`nearHotels`) and the first leg's duration. Unknowns always sort
  // last.
  'plannerPrice',
  'roomNightsSold',
  'plannerDistance',
  'plannerDuration',
] as const;
export type ProductSortField = (typeof productSortFields)[number];

export const findProductsInputSchema = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.enum(productSortFields).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  // Free-text search — service splits on whitespace and ILIKEs across
  // titleEn, title::text, city, suppliers::text per legacy handleTitleFilter.
  title: z.string().optional(),
  city: z.string().optional(),
  // Storefront free-text search — per-token AND across the product title (any
  // language), city and country. Deliberately a separate field from `title`:
  // that one is the console's omni box and also reaches labels and suppliers,
  // which a public visitor has no business matching on. Both `products.list`
  // implementations honour it identically (see
  // `apps/products/api/src/products/search-sql.ts`).
  search: z.string().optional(),
  // Per-array filters: arrays of zero length are treated as "no filter".
  contentCategory: z.array(z.enum(categoryEnumValues)).optional(),
  contentSubcategory: z.array(z.enum(subcategoryEnumValues)).optional(),
  catalogId: z.array(z.uuid()).optional(),
  // Leaves out every catalog with one of these titles (case-insensitive),
  // e.g. the agent portal's Mini Planner leaving out "Audley".
  excludeCatalogTitles: z.array(z.string().trim().min(1)).optional(),
  // `true`: leaves out every product the house never offers by its title —
  // marked do-not-use, Audley's, a template (`forbiddenReason` in
  // `@travel-planner/recommendation`, on the house's title: English, else the
  // first, else `title_en`). The
  // agent's search sets it (N2-10).
  excludeForbiddenTitles: z.literal(true).optional(),
  // Country accepts ISO codes/labels AND the legacy "Other" sentinel which
  // matches NULL country rows. Service unwraps it.
  country: z.array(z.string()).optional(),
  // CMS-discovery hashtag filter. Matches products whose `tags` jsonb array
  // contains ANY of these names (case-insensitive overlap). Empty array =
  // no filter. Backs the AIT-CMS mood/landing hashtag pickers via the
  // service-account caller.
  hashtags: z.array(z.string()).optional(),
  // Category-hashtag relation filter. Matches products linked via
  // product_hashtag → category_hashtag.normalized_name. Empty array =
  // no filter. Distinct from `hashtags` (unused product.tags jsonb).
  categoryHashtags: z.array(z.string()).optional(),

  // Visibility — `true` = hidden only, `false` = visible only, omitted = both
  // (matches legacy FindProductsListDto.isHidden semantics).
  isHidden: z.boolean().optional(),
  // CMS visibility cascades through catalog.isExcludedFromCms in the
  // service; see applyCmsVisibilityFilter in legacy product.service.ts.
  isHiddenOnCms: z.boolean().optional(),
  // `true`: only what the CMS storefront shows — the product not hidden there
  // (no flag counts as shown) and its catalog not excluded, unless the
  // product is explicitly shown. Stricter `isHiddenOnCms: false` reads no
  // flag as hidden.
  visibleInCms: z.literal(true).optional(),
  // Mini Planner visibility — `false` = shown there, `true` = hidden there,
  // omitted = both. Product-level only (no catalog cascade).
  isHiddenOnMiniPlanner: z.boolean().optional(),
  // Geocoded-or-not filter. `false` = rows with NO usable point, which means
  // `location_point IS NULL` **or** the legacy `(0,0)` null-island sentinel —
  // the same predicate the geocode backfill uses to find work
  // (apps/products/api/src/runtime/location-{backfill-cron,handler}.ts).
  // `true` = rows with a real point. Omitted = no filter.
  hasCoordinates: z.boolean().optional(),
  // Include subitem-template products. Default false (matches legacy default
  // where subitems are filtered out of the standard list).
  includeSubItems: z.boolean().optional(),
  // Geo — 4-corner bbox OR (preferred) full 8-point polygon when all
  // northEast/southEast/northWest/southWest pairs are provided.
  bbox: productBboxSchema.optional(),
  northWestLat: z.number().optional(),
  northWestLng: z.number().optional(),
  southEastLat: z.number().optional(),
  southEastLng: z.number().optional(),
  // Polygon regions defined per organization — ST_Contains against
  // custom_region.boundary_geometry. Array form (ANY-of): the service
  // EXISTS-joins where `id = ANY(customRegionIds)`. Empty / absent = no
  // region filter. Replaces the legacy singular `customRegionId` —
  // cms-public mood-picker passes multiple regions; legacy callers
  // wrap their single id in an array.
  customRegionIds: z.array(z.uuid()).optional(),
  // Budget-tier filter (1..4). Restricts to Experience categories only —
  // see EXPERIENCE_CATEGORIES. Returns empty until the pricing track
  // populates budgetValue / defaultBudgetValue.
  valueTier: z.array(z.number().int().min(1).max(4)).optional(),
  // Exact supplier-name filter (substring match against the suppliers jsonb
  // serialised form, per legacy parity — known false-positive risk).
  supplierName: z.string().optional(),
  // Products linked to one CRM supplier account — backs the supplier record's
  // Linked Products tab (apps/crm/prd/crm-supplier-record.md §10). The service
  // matches BOTH shapes stored in `suppliers`: {companyId, name} (subitem
  // payload adapter) and {id, name, crmType: 1} (product Supplier tab).
  supplierCompanyId: z.uuid().optional(),
  // Proximity anchor [lat, lng]. When set, the service appends a 100km
  // radius condition and sorts by ST_DistanceSphere asc (after the
  // thumbnail-first tier).
  hotelLocationPoint: z.tuple([z.number(), z.number()]).optional(),
  // Mini Planner chapters' hotels, for `sortBy: 'plannerDistance'`: a
  // product's distance is to the hotel of the region whose outline holds its
  // point; a product in none of these regions has no distance.
  nearHotels: z
    .array(
      z.object({
        regionId: z.uuid(),
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
      })
    )
    .max(30)
    .optional(),
  // Products whose card details have this pace come first (the brief's pace
  // in the Mini Planner); the rest keep their order after them.
  preferPace: z.enum(PACES).optional(),
  // Mini Planner hotel filters: the star class (4 matches 4 and 4.5; the
  // unrated 1.0 matches none) and the price level from the sold average per
  // room night (`hotelPriceLevel`, € to €€€€). ANDed with the rest.
  hotelStars: z.array(z.number().int().min(2).max(5)).max(4).optional(),
  hotelPriceLevels: z.array(z.number().int().min(1).max(4)).max(4).optional(),
  // Only these products — e.g. the planner pricing recommender picks with the
  // same row the result cards use. ANDs with every other filter; empty /
  // absent = no id filter.
  ids: z.array(z.uuid()).max(100).optional(),
  // With `customRegionIds`, the bbox normally gives way to the region (the
  // console planner's chapter scope). `true` keeps both: the map area
  // narrows within the region.
  bboxWithinRegion: z.boolean().optional(),
});

export type FindProductsInput = z.infer<typeof findProductsInputSchema>;

/**
 * What a travel agent may find in the Mini Planner (TCCMS03-078): visible
 * products the CMS storefront would also show (no flag counts as shown), not
 * hidden from the Mini Planner (XT-890), outside the catalogs kept for other
 * channels. One definition for every door that serves an agent the Mini
 * Planner's catalogue — the portal and Claude (XT-953, XTAPL-14).
 */
export const MINI_PLANNER_VISIBILITY: Readonly<
  Required<
    Pick<
      FindProductsInput,
      | 'isHidden'
      | 'visibleInCms'
      | 'isHiddenOnMiniPlanner'
      | 'excludeCatalogTitles'
    >
  >
> = Object.freeze({
  isHidden: false,
  visibleInCms: true,
  isHiddenOnMiniPlanner: false,
  excludeCatalogTitles: Object.freeze(['Audley']) as string[],
});

// Lightweight count-by-hashtags input for the cms-public hero-count line
// (M11). Reuses the discovery filters — hashtags + optional country +
// optional region restriction — and returns just the total. No paging,
// no projection. Backs `products.countByHashtags`.
export const countProductsByHashtagsInputSchema = z.object({
  hashtags: z.array(z.string()).min(1),
  // Section content-category (e.g. ['Accommodation']). Scopes both the
  // hashtag-association expansion target categories AND the counted rows so
  // the hero count equals the per-section discovery `countAll`. Omitted =
  // count across all categories (legacy ProductCountByHashtagsParams always
  // sends one).
  contentCategory: z.array(z.enum(categoryEnumValues)).optional(),
  country: z.string().optional(),
  customRegionIds: z.array(z.uuid()).optional(),
  // Same CMS-visibility default as the discovery list: hidden rows
  // never count toward hero totals.
  isHiddenOnCms: z.boolean().default(false),
  // Budget tier, as `discover`'s, so the hero count matches the section.
  valueTier: z.array(z.number().int().min(1).max(4)).optional(),
});
export type CountProductsByHashtagsInput = z.infer<
  typeof countProductsByHashtagsInputSchema
>;

export const countProductsByHashtagsResultSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type CountProductsByHashtagsResult = z.infer<
  typeof countProductsByHashtagsResultSchema
>;

// products.discover — full-fidelity CMS hashtag discovery (port of
// ait-cms-api ProductService.find()'s hashtag branch). Unlike `list`'s
// simple `tags`-jsonb overlap, discovery joins the product↔category_hashtag
// relation on `normalized_name`, expands the input names via the
// hashtag_association table, scores + orders by association/custom/ai score,
// then backfills (co-occurring hashtags) and random-fills up to `limit` so a
// section never renders empty. Returns the same compact list-row shape as
// `list`. `countAll` reflects the *relation-matched* total (pre-backfill),
// matching legacy `find()` + `countByHashtags`.
export const discoverProductsInputSchema = z.object({
  hashtags: z.array(z.string()).min(1),
  contentCategory: z.array(z.enum(categoryEnumValues)).optional(),
  contentSubcategory: z.array(z.enum(subcategoryEnumValues)).optional(),
  catalogId: z.array(z.uuid()).optional(),
  country: z.array(z.string()).optional(),
  customRegionIds: z.array(z.uuid()).optional(),
  isHiddenOnCms: z.boolean().default(false),
  // Budget tier (1..4 == $..$$$$), as `list`'s: Experience categories only.
  valueTier: z.array(z.number().int().min(1).max(4)).optional(),
  limit: z.number().int().positive().max(100).default(6),
  offset: z.number().int().nonnegative().default(0),
});
export type DiscoverProductsInput = z.infer<typeof discoverProductsInputSchema>;

// products.recommend / recommendations.similarProducts — weighted-similarity
// recommendation port (legacy `RecommendService.similarProducts`). Returns
// `productListItemSchema[]` so cms-public can render with its existing
// `mapListItemToProduct` consumer without a second mapper.
export const similarProductsInputSchema = z.object({
  sourceId: z.uuid(),
  // Top-N to return; caller caps at 24 (same bound as the discovery
  // server fns) so wire payloads stay small.
  count: z.number().int().positive().max(24).default(5),
  // Candidate pool the chain scores over. Legacy was unbounded; cap to
  // 200 to keep latency predictable on large catalogues. Lift via the
  // input override when an admin surface needs full coverage.
  candidateLimit: z.number().int().positive().max(500).default(200),
  // Match the cms-public default: hide CMS-hidden rows from public-site
  // recommendations.
  isHiddenOnCms: z.boolean().default(false),
});
export type SimilarProductsInput = z.infer<typeof similarProductsInputSchema>;

// The `product.avg_room_night` jsonb bag — the average client SELL PRICE per room, per
// night for a hotel, rolled up from the accommodation items of SOLD
// itineraries by `pnpm products:hotel-avg-room-night`.
//
// Derived data, rewritten wholesale by that command: never patched by a
// request handler. `null` on the product means "no sold history yet", and the
// planner shows nothing rather than a zero.
//
// The average deliberately blends room TYPES together (a hotel with Double and
// Suite rows reports one figure) and is weighted by room-nights. Always EUR.
export const hotelAvgRoomNightSchema = z.object({
  /** Weighted average client sell price per room per night, in EUR, 2dp. */
  avgSellPriceEur: z.number(),
  /** Priced room groups behind the average — the UI says "from N bookings". */
  sampleSize: z.number().int().nonnegative(),
  /** Total room-nights those groups represent (the weighting denominator). */
  roomNights: z.number().int().nonnegative(),
  /** Sale date of the most recent itinerary feeding it — how stale it is. */
  lastSoldOn: z.string().nullable(),
  /** When the roll-up last ran. */
  computedOn: z.string(),
});
export type HotelAvgRoomNight = z.infer<typeof hotelAvgRoomNightSchema>;

// Compact list-row output — mirrors legacy ProductListItemDto field-for-field
// (legacy product/dto/product-list-item.dto.ts). Keeps the wire payload small
// and avoids leaking jsonb internals (details, attachments, etc.) to the
// client. titleEn / catalogId / modifiedOn are intentionally NOT here —
// legacy `BASE_PRODUCT_SELECT_FIELDS` (service.ts:48) doesn't return them on
// list responses.
export const productListItemSchema = z.object({
  id: z.uuid(),
  title: z.json(),
  thumbnail: z.string().nullable(),
  // Marketing hero — uploadthing fileKey. cms-public prefers this over
  // `thumbnail` when populated. Nullable: legacy products predate the
  // cms-product-marketing surface.
  cmsHero: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  // jsonb on the DB; opaque to the renderer, but a concrete JSON tree so
  // server-fn boundaries accept it (they reject `unknown`).
  price: z.json(),
  currency: z.string().nullable(),
  // Raw Drizzle geometry: a PostGIS-order `[lng, lat]` tuple (longitude
  // first), or null. Read it through a normaliser such as the products map's
  // `normalizeProductPoint`, never as [lat, lng].
  locationPoint: z
    .object({ lat: z.number(), lng: z.number() })
    .or(z.tuple([z.number(), z.number()]))
    .nullable(),
  // Minimal legs shape: only details.startTime/endTime, post-processed by
  // the service to mirror legacy findProductsList:211-217.
  legs: z
    .array(
      z.object({
        details: z.object({
          startTime: z.json().optional(),
          endTime: z.json().optional(),
        }),
      })
    )
    .nullable(),
  // numeric(2,1) → string when read out of Drizzle.
  stars: z.string().nullable(),
  // What `price` is per — `pricing_type.type`: Pax, Night, Piece, Unit, Day,
  // Hour, Vehicle. Optional: not every list implementation returns it.
  pricingType: z.string().nullable().optional(),
  isHidden: z.boolean(),
  isHiddenOnCms: z.boolean().nullable(),
  // Optional: not every list implementation returns it.
  isHiddenOnMiniPlanner: z.boolean().optional(),
  privateNotes: z.string().nullable(),
  // Hashtag array (legacy ProductTagDto[]). Stored as jsonb `tags` column.
  tags: z.json(),
  // The `hashtags` column the catalogue actually fills (Urban, FineDining…);
  // `tags` above is mostly empty. Optional: not every list returns it.
  hashtags: z.array(z.string()).nullable().optional(),
  // The English short marketing text, for the planner card's one-line summary.
  shortMarketingTextEn: z.string().nullable().optional(),
  createdBy: z.uuid().nullable(),
  hyperGuestHotelId: z.number().int().nullable(),
  // Average client sell price per room per night from sold itineraries — see
  // `hotelAvgRoomNightSchema`. Only ever populated for Accommodation products,
  // and null until the roll-up has enough sold history. Optional because older
  // payloads predate it; the agent portal shows it too (XT-827).
  avgRoomNight: hotelAvgRoomNightSchema.nullable().optional(),
  // The planner card's facts (rooms, format, pace…). Optional: not every list
  // implementation returns it.
  cardFacts: cardFactsSchema.nullable().optional(),
  // Recommendation boost (numeric column). Optional/omitted on most list
  // surfaces; `products.findSimilar` surfaces it so the recommendations
  // explorer can prefill the boost-edit modal (legacy parity). null → 0
  // is applied client-side.
  boost: z.number().optional(),
});

export type ProductListItem = z.infer<typeof productListItemSchema>;

// Per-filter scoring breakdown for one suggestion — ports legacy
// `AppliedFilter` (recommend/interface/applied-filter.interface.ts). `score`
// is the weighted contribution to the total; `targetScore` is the filter's
// raw 0–100 result (−1 = chain broken).
export const appliedFilterScoreSchema = z.object({
  name: z.string(),
  score: z.number(),
  targetScore: z.number(),
  message: z.string().optional(),
});
export type AppliedFilterScore = z.infer<typeof appliedFilterScoreSchema>;

// A scored recommendation — a `ProductListItem` enriched with the weighted
// total `score`, the per-filter `filterScores` breakdown, and any
// `filterMessages`. Mirrors legacy `SimilarProductType` so the explorer's
// suggestions table can render the Filters-Scoring + Score columns.
export const recommendationSuggestionSchema = productListItemSchema.extend({
  score: z.number(),
  filterScores: z.array(appliedFilterScoreSchema),
  filterMessages: z.array(z.string()),
});
export type RecommendationSuggestion = z.infer<
  typeof recommendationSuggestionSchema
>;

// Pagination wrapper — legacy PaginationOutputDto<T> shape.
export const paginatedProductsSchema = z.object({
  data: z.array(productListItemSchema),
  count: z.number(),
  countAll: z.number(),
});

// `productsRouter.findSimilar` input. Matches the legacy
// `recommend.controller#similar` endpoint shape: a single productId,
// no pagination — the legacy scoring chain returns the top 5
// scored matches and that's the planner picker's contract too.
export const findSimilarProductsInputSchema = z.object({
  productId: z.uuid(),
});

export type FindSimilarProductsInput = z.infer<
  typeof findSimilarProductsInputSchema
>;

export type PaginatedProducts = z.infer<typeof paginatedProductsSchema>;

// ---------------------------------------------------------------------------
// products.findAll — the legacy `/products` endpoint. Same filters as list
// but returns a wider row shape (description, attachments, videos, ...) and
// uses fixed legacy ordering (thumbnail desc, proximity if anchored, then
// modifiedOn desc). No user sortBy/sortOrder knob.
// ---------------------------------------------------------------------------

export const findProductsExtendedInputSchema = findProductsInputSchema.omit({
  sortBy: true,
  sortOrder: true,
});
export type FindProductsExtendedInput = z.infer<
  typeof findProductsExtendedInputSchema
>;

// Extended row — adds the columns legacy findAll's wider SELECT exposes
// (BASE_PRODUCT_SELECT_FIELDS + catalogId/accountId/attachments/description/
// videos/suppliers/facilities/transportType/serviceType/pricingType/isGross/
// costVatId/priceVatId — see legacy product.service.ts:259-275). Legacy
// findAll returns raw legs jsonb (no trim like list does), so widen legs
// back to `unknown` here.
export const productExtendedListItemSchema = productListItemSchema.extend({
  legs: z.unknown(),
  description: z.unknown(),
  attachments: z.unknown(),
  videos: z.unknown(),
  suppliers: z.unknown(),
  // Raw jsonb facility UUID array (legacy product.facilities column).
  facilities: z.unknown(),
  // Denormalized facility detail (legacy QueryUtils.selectWithFacility) —
  // `{ id, title }[]` resolved from the UUID array. NULL when the product
  // has no facilities. Surfaced on findAll responses to avoid a round-trip
  // per row from the consumer.
  facilitiesDetail: z
    .array(z.object({ id: z.uuid(), title: z.unknown() }))
    .nullable(),
  catalogId: z.uuid(),
  accountId: z.uuid().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  pricingType: z.string().nullable(),
  isGross: z.boolean().nullable(),
  costVatId: z.uuid().nullable(),
  priceVatId: z.uuid().nullable(),
  parentId: z.uuid().nullable(),
  isSubitem: z.boolean(),
  // Internal product reference (slug-ish). Used by subitemTemplates to sort
  // subitems within a parent template in a stable, user-meaningful order.
  ref: z.string(),
  // JSON details bag — subitem-form fields (venue, priority, languageCode,
  // pickup, dropoff, meetingPoint, labels, …) live here. Opaque to the API
  // contract; the form layer types it per category.
  details: z.unknown().nullable(),
  // Top-level cost / netCost numeric columns (separate from the `price`
  // jsonb). Drizzle's `numeric` maps to string on the wire.
  cost: z.string().nullable(),
  netCost: z.string().nullable(),
});

export type ProductExtendedListItem = z.infer<
  typeof productExtendedListItemSchema
>;

export const paginatedProductsExtendedSchema = z.object({
  data: z.array(productExtendedListItemSchema),
  count: z.number(),
  countAll: z.number(),
});

export type PaginatedProductsExtended = z.infer<
  typeof paginatedProductsExtendedSchema
>;

// ---------------------------------------------------------------------------
// products.hotels — legacy `/products/hotels` (product.service.ts:1095).
// Filters to Accommodation products (any subcategory) server-side. The only accepted input
// is `hasHyperGuestId` (when true, restricts to rows with a non-null
// `hyperGuestHotelId`). No pagination — legacy returns the whole array.
// ---------------------------------------------------------------------------

export const findHotelsInputSchema = z.object({
  hasHyperGuestId: z.boolean().optional(),
});
export type FindHotelsInput = z.infer<typeof findHotelsInputSchema>;

// Compact projection for the hotels endpoint — matches legacy
// product.service.ts:1099-1108. Distinct from the list-row shape because the
// hotel-availability flow needs `priceCategory` and a different field set.
export const hotelListItemSchema = z.object({
  id: z.uuid(),
  title: z.unknown(),
  // Product thumbnail (uploadthing key/URL) — resolved via `resolveThumbnailUrl`
  // on the client, same source the planner Products list uses.
  thumbnail: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  priceCategory: z.number().int().nullable(),
  hyperGuestHotelId: z.number().int().nullable(),
  legs: z.unknown(),
  locationPoint: z
    .object({ lat: z.number(), lng: z.number() })
    .or(z.tuple([z.number(), z.number()]))
    .nullable(),
});
export type HotelListItem = z.infer<typeof hotelListItemSchema>;

// ---------------------------------------------------------------------------
// products.subitems — legacy `/products/subitems` (product.service.ts:132).
// Pagination-only input — no filters. Service forces isSubitem=true. Use
// products.subitemTemplates for parentId-scoped listings.
// ---------------------------------------------------------------------------

export const findSubItemsInputSchema = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
});
export type FindSubItemsInput = z.infer<typeof findSubItemsInputSchema>;

// Compact subitem-row projection mirroring legacy findAllSubitems:137-148.
export const subItemListItemSchema = z.object({
  id: z.uuid(),
  title: z.unknown(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  thumbnail: z.string().nullable(),
  currency: z.string().nullable(),
  price: z.unknown(),
  locationPoint: z
    .object({ lat: z.number(), lng: z.number() })
    .or(z.tuple([z.number(), z.number()]))
    .nullable(),
});
export type SubItemListItem = z.infer<typeof subItemListItemSchema>;

export const paginatedSubItemsSchema = z.object({
  data: z.array(subItemListItemSchema),
  count: z.number(),
  countAll: z.number(),
});
export type PaginatedSubItems = z.infer<typeof paginatedSubItemsSchema>;

// ---------------------------------------------------------------------------
// products.filterCounts — legacy `/products/filter-counts`. Aggregated
// counts powering the list page's filter sidebar. Each dimension reflects
// the totals that *would* result if the user toggled values within that
// dimension; the other dimensions' active filters still apply.
// ---------------------------------------------------------------------------

// Legacy getProductFilterCounts (product.service.ts:1130) accepts a narrow
// subset of the list filters — catalogId | city | country | bbox/polygon |
// isHiddenOnCms | valueTier. Notably NOT title, category/subcategory,
// supplierName, customRegionIds, hotelLocationPoint, includeSubItems.
export const findFilterCountsInputSchema = z.object({
  catalogId: z.array(z.uuid()).optional(),
  city: z.string().optional(),
  country: z.array(z.string()).optional(),
  bbox: productBboxSchema.optional(),
  northWestLat: z.number().optional(),
  northWestLng: z.number().optional(),
  southEastLat: z.number().optional(),
  southEastLng: z.number().optional(),
  isHiddenOnCms: z.boolean().optional(),
  valueTier: z.array(z.number().int().min(1).max(4)).optional(),
});
export type FindFilterCountsInput = z.infer<typeof findFilterCountsInputSchema>;

const filterCountSchema = z.object({
  id: z.string().nullable(),
  title: z.string(),
  count: z.number(),
});

const filterCountWithChildrenSchema = filterCountSchema.extend({
  children: z.array(filterCountSchema),
});

export const productFilterCountsSchema = z.object({
  countries: z.array(filterCountSchema),
  catalogs: z.array(filterCountSchema),
  categories: z.array(filterCountWithChildrenSchema),
  // Always 4 rows (`'1'..'4'`) — when the pricing track has not populated the
  // budget-value columns, counts are 0.
  valueTiers: z.array(filterCountSchema),
});
export type ProductFilterCounts = z.infer<typeof productFilterCountsSchema>;

// ---------------------------------------------------------------------------
// Write surface — schemas powering products.create / update (widened) /
// remove / duplicate / move and the batch endpoints. The corresponding
// `updatedProductSchema` (read-side) is the canonical output shape; bulk
// writes return the small `{ success, count }` envelope instead.
// ---------------------------------------------------------------------------

// Attachment row stored on `product.attachments` (jsonb). Mirrors legacy
// product/dto/product-attachment.dto.ts — every field optional except for the
// UploadThing pointers which the consumer is expected to supply.
export const productAttachmentSchema = z.object({
  fileKey: z.string().optional(),
  url: z.string().optional(),
  id: z.string().optional(),
  file: z
    .object({
      name: z.string().optional(),
      size: z.number().optional(),
      contentType: z.string().optional(),
      content: z.string().optional(),
    })
    .optional(),
  type: z.string().optional(),
  showExternally: z.boolean().optional(),
  isThumbnail: z.boolean().optional(),
  fileNameBase: z.string().optional(),
  caption: z.string().optional(),
  order: z.number().optional(),
});
export type ProductAttachment = z.infer<typeof productAttachmentSchema>;

// Supplier row stored on `product.suppliers` (jsonb). Loose object to match
// the legacy SupplierDto — every consumer treats it as opaque jsonb.
const supplierSchema = z.looseObject({
  name: z.string().optional(),
  companyId: z.string().optional(),
});

// ─── Product preview: supplier CRM documents (Melhoria 6) ────────────────────
//
// The product preview's Attachments tab shows the linked CRM supplier's
// documents ABOVE the product's own. They are read live from the supplier
// record on every request — never copied onto the product — so an update in CRM
// is immediately visible to every linked product (§3).
//
// The two sources stay SEPARATE collections rather than one merged list: the UI
// labels each document's origin and the permission check differs per source, so
// flattening them would lose the distinction the spec is built on (§9).
export const productPreviewSupplierDocumentSchema = z.object({
  documentId: z.string(),
  name: z.string(),
  category: z.string(),
  // 'Internal' | 'ProductAvailable' as stored on the CRM row. Informational in
  // the preview — it is read-only and never changes the setting (§6).
  visibility: z.string(),
  featured: z.boolean(),
  fileType: z.string(),
  updatedAt: z.string(),
});

export type ProductPreviewSupplierDocument = z.infer<
  typeof productPreviewSupplierDocumentSchema
>;

// `linked: false` with `supplierId: null` is the "no CRM supplier" case, which
// the UI renders as a message rather than an empty table (§8). A linked
// supplier that resolved but holds no documents is `linked: true` with an empty
// array — a genuinely different state, so the two must not collapse.
export const productPreviewSupplierDocumentsSchema = z.object({
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  linked: z.boolean(),
  // True when the product references a supplier the server could not resolve
  // (soft-deleted, out of org, or a dangling id). Distinct from `linked: false`,
  // which means the product never named one.
  unavailable: z.boolean(),
  documents: z.array(productPreviewSupplierDocumentSchema),
});

export type ProductPreviewSupplierDocuments = z.infer<
  typeof productPreviewSupplierDocumentsSchema
>;

// The record whose supplier a preview reads: a catalogue product, or an
// itinerary item (T-287 — the planner's item preview). Either way the server
// reads the supplier from that record's own `suppliers`, never from the caller.
export const previewSupplierSourceInput = z.union([
  z.object({ productId: z.uuid() }),
  z.object({ itineraryItemId: z.uuid() }),
]);

export type PreviewSupplierSource = z.infer<typeof previewSupplierSourceInput>;

export const productPreviewSupplierDocumentsInput = previewSupplierSourceInput;

// Melhoria 6 §10: opening a CRM document FROM a preview. The caller names the
// source record and the document — never the supplier — so the server resolves
// the supplier from that record's own relationship and a tampered `documentId`
// can only ever match a document of the supplier the record references.
export const productPreviewSupplierDocumentUrlInput = z.union([
  z.object({ productId: z.uuid(), documentId: z.string() }),
  z.object({ itineraryItemId: z.uuid(), documentId: z.string() }),
]);

// T-196: the linked CRM supplier's commercial context shown in the product
// preview's Private Notes tab — the supplier's Contract Year and its CRM Notes.
//
// Read live from `crm.company` on every request, exactly like
// `previewSupplierDocuments`, so an edit in CRM is reflected on every product
// linked to that supplier with no copy stored on the product side.
//
// The `linked` / `unavailable` pair carries the same three-state meaning as the
// documents schema above and for the same reason: "this product names no CRM
// supplier" and "the supplier it names cannot be resolved" are different facts,
// and the UI states them differently. Collapsing them would report a
// data-quality problem as a deliberate absence.
export const productPreviewSupplierNotesSchema = z.object({
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  linked: z.boolean(),
  unavailable: z.boolean(),
  // The year of the supplier's latest valid commercial agreement or rate set
  // (`crm.company.contract_year`). Nullable — the field is optional in CRM.
  contractYear: z.number().nullable(),
  // `crm.company.notes` — the supplier record's own internal notes. Distinct
  // from the PRODUCT's `privateNotes`, which the same tab renders separately.
  notes: z.string().nullable(),
});

export type ProductPreviewSupplierNotes = z.infer<
  typeof productPreviewSupplierNotesSchema
>;

export const productPreviewSupplierNotesInput = previewSupplierSourceInput;

// Tag row stored on `product.tags` (jsonb). Loose object — the edit forms
// write the legacy `{ name }` shape, so neither key is required.
const productTagInputSchema = z.looseObject({
  name: z.string().optional(),
  id: z.string().optional(),
});

// Group-price thresholds — Activity Group pricing rows. Each threshold maps a
// people-count band (minPeople..maxPeople, maxPeople=null means "and above")
// to a price, optionally scoped to a traveller type. Optional `additionalPrice`
// lets the row charge a different rate to a secondary traveller type (e.g.
// child) within the same band. Cross-row non-overlap is enforced by the
// products.update service.
//
// `travellerId` is OPTIONAL, and blank/null is meaningful — it is the
// "All travellers" category, priced over the item's TOTAL head count. See
// `resolveGroupCategoryKey` in packages/shared/src/pricing/engine.ts
// (`if (!travellerId) return null`) and the chain count it feeds:
//   chainCount = key === null ? travellerCounts.total
//                             : (travellerCounts.byTravellerId.get(key) ?? 0)
// so a blank band charges every traveller while an Adults band charges only
// the adults. Both adapters, both engine schemas and the itinerary threshold
// editor already model it that way (apps/itineraries/prd/tests/pricing-table.md
// TCP-PR01-3047, "All = total travellers"); this write surface was the sole
// outlier and rejected the blank the products UI actually sends (XT-660).
// Do NOT "fix" a blank by substituting a placeholder traveller id: an id that
// resolves to no head count yields chainCount 0, which bills nothing and
// raises no diagnostic.
export const groupPriceThresholdSchema = z
  .object({
    minPeople: z.number().int().min(1),
    maxPeople: z.number().int().min(1).nullable(),
    price: z.number().nonnegative(),
    travellerId: z.uuid().nullable().optional(),
    additionalPrice: z.number().nonnegative().nullable().optional(),
    // Blank is legal here too. Unlike `travellerId` this is never a chain key
    // — `engine.ts` uses it only as a selector inside an already-resolved
    // chain — so a blank cannot regroup bands; it just prices the additional
    // traveller in the adult bucket (`resolveTravellerType` defaults to
    // 'adult'). Pairing it with `additionalPrice` used to be enforced here and
    // failed the same save as XT-660, unrecoverably (the Select had no empty
    // option), so the pairing is no longer a wire rule.
    additionalTravellerId: z.uuid().nullable().optional(),
    includeAdditional: z.boolean().optional(),
  })
  .refine((t) => t.maxPeople === null || t.maxPeople >= t.minPeople, {
    message: 'maxPeople must be >= minPeople or null',
  });
export type GroupPriceThreshold = z.infer<typeof groupPriceThresholdSchema>;

// Latitude/longitude tuple for `product.locationPoint`. Legacy DTO accepts
// `[lng, lat]` (LocationPoint); we keep the same tuple order so the service
// can pass it straight through to PostGIS via ST_GeomFromText.
const locationPointTupleSchema = z.tuple([z.number(), z.number()]);

// Localized title/description array — same shape as facility.title.
const localizedTextArraySchema = z.array(productLocalizedTextSchema);

// Leg row — jsonb. Opaque to the write surface (the itinerary planner owns
// its shape); validation lives downstream when individual legs are mutated.
const productLegInputSchema = z.looseObject({});

// Detail (single object on `product.details` jsonb). Opaque.
const productDetailInputSchema = z.looseObject({});

// Currency / pricing-type / transport-type / service-type — accept any
// non-empty string. Legacy uses TypeScript enums; in this monorepo these are
// stored as `text` columns with no DB-level enum, so we stay permissive at
// the wire and rely on downstream validators.
const looseEnumString = z.string().min(1);

// Price body — discriminated by which key(s) are present. Stored verbatim on
// product.price (jsonb). Three variants match the legacy at-rest shape per
// PRD apps/products/prd/product-edit/05-tab-pricing.md §"API mapping":
//   - Single (Unit/Piece/Hour/Day/Vehicle): { cost, netCost, price, markup,
//     commission, units? } — one flat row, no `rooms`/`travellers` key.
//   - Travellers (Pax mode): { travellers: PaxRow[] }.
//   - Rooms (Accommodation): { rooms: RoomRow[] }.
// Math (recompute of netCost/markup/derived display fields) lives on the BE
// pricing engine (packages/shared/src/pricing/) and is invoked separately
// from the FE compute procedure — at the wire we only carry raw inputs.
const pricingRowMoneyShape = {
  cost: z.number().nonnegative().optional(),
  netCost: z.number().nonnegative().optional(),
  price: z.number().nonnegative().optional(),
  markup: z.number().optional(),
  commission: z.number().optional(),
  units: z.number().int().nonnegative().optional(),
  // Per-row VAT overrides. When present they take precedence over the
  // product-level `costVatId` / `priceVatId` for compute + display. Stored
  // alongside the row money fields in the price jsonb so the legacy
  // top-level columns stay as the "default" / consumer fallback.
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
} as const;

export const singleProductPriceSchema = z.object({
  ...pricingRowMoneyShape,
});
export type SingleProductPrice = z.infer<typeof singleProductPriceSchema>;

export const paxProductPriceRowSchema = z.object({
  travellerId: z.uuid(),
  ...pricingRowMoneyShape,
});
export type PaxProductPriceRow = z.infer<typeof paxProductPriceRowSchema>;

export const travellersProductPriceSchema = z.object({
  travellers: z.array(paxProductPriceRowSchema).min(1),
});
export type TravellersProductPrice = z.infer<
  typeof travellersProductPriceSchema
>;

export const productRoomPricePeriodSchema = z.object({
  startNight: z.number().int().min(1),
  nightCount: z.number().int().min(1),
  cost: z.number().nonnegative(),
  netCost: z.number().nonnegative().optional(),
  price: z.number().nonnegative(),
  markup: z.number().optional(),
  commission: z.number().optional(),
});
export type ProductRoomPricePeriod = z.infer<
  typeof productRoomPricePeriodSchema
>;

const roomTravellerAssignmentSchema = z.object({
  travellerId: z.uuid(),
  quantity: z.number().int().nonnegative(),
});

export const productRoomPriceSchema = z.object({
  roomType: z.string().min(1),
  ...pricingRowMoneyShape,
  travellers: z.array(roomTravellerAssignmentSchema).optional(),
  pricePeriods: z.array(productRoomPricePeriodSchema).optional(),
});
export type ProductRoomPrice = z.infer<typeof productRoomPriceSchema>;

export const roomsProductPriceSchema = z.object({
  rooms: z.array(productRoomPriceSchema).min(1),
});
export type RoomsProductPrice = z.infer<typeof roomsProductPriceSchema>;

// Union of the three at-rest shapes. Each variant has a distinguishing key
// (`rooms` / `travellers`) so the union resolves unambiguously; the Single
// shape is the fallback (neither key present).
const productPriceInputSchema = z
  .union([
    roomsProductPriceSchema,
    travellersProductPriceSchema,
    singleProductPriceSchema,
  ])
  .nullable()
  .optional();
export type ProductPriceInput = z.infer<typeof productPriceInputSchema>;

// All product fields that create/update can write. Build the shape once and
// reuse it for both schemas — `createProductInput` requires the small set
// the legacy DTO marks as @IsDefined; `updateProductPatchSchema` makes them
// optional and adds the "at least one field present" refine.
const productWritableFields = {
  title: localizedTextArraySchema.optional(),
  description: localizedTextArraySchema.optional(),
  legs: z.array(productLegInputSchema).optional(),
  details: productDetailInputSchema.optional(),
  attachments: z.array(productAttachmentSchema).optional(),
  thumbnail: z.string().optional(),
  reusable: z.boolean().optional(),
  isSubitem: z.boolean().optional(),
  parentId: z.uuid().optional(),
  originalContentProviderId: z.uuid().optional(),
  suppliers: z.array(supplierSchema).optional(),
  tags: z.array(productTagInputSchema).optional(),
  currency: looseEnumString.optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  locationPoint: locationPointTupleSchema.optional().nullable(),
  boost: z.number().optional(),
  supplierValue: z.number().optional(),
  videos: z.array(z.string()).optional(),
  clonedFromId: z.uuid().optional(),
  accountId: z.string().optional(),
  originalExternalId: z.string().optional(),
  facilities: z.array(z.uuid()).optional(),
  // Only where this product's outdoor mark differs from the facility's.
  facilityOutdoor: z.record(z.uuid(), z.boolean()).nullable().optional(),
  stars: productStarsSchema.optional(),
  budgetValue: z.number().int().min(1).max(4).nullable().optional(),
  pricingUnits: z.number().int().optional(),
  pricingQuantity: z.number().int().optional(),
  pricingItemVariantId: z.uuid().optional(),
  pricingItemNote: z.string().optional(),
  pricingTypeOverrideId: z.uuid().optional(),
  notes: z.string().optional(),
  privateNotes: z.string().optional(),
  advisorNotes: z.string().optional(),
  cardFacts: cardFactsSchema.nullable().optional(),
  serviceId: z.uuid().optional(),
  videosOnTop: z.boolean().optional(),
  externalProductId: z.string().nullable().optional(),
  pricingType: looseEnumString.optional(),
  isGross: z.boolean().optional(),
  // `null` = 0% VAT; it must be writable or a cleared rate can never be saved.
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  transportType: looseEnumString.optional(),
  serviceType: looseEnumString.optional(),
  priceCategory: z.number().int().optional(),
  price: productPriceInputSchema,
  curatedUrls: z.array(z.string()).optional(),
  hyperGuestHotelId: z.number().int().optional(),
  // Cleared via `null` from the HyperGuest admin Mapper when the user
  // rejects a suggested match (admin-managed; not exposed in the regular
  // product-edit forms).
  unconfirmedHyperGuestId: z.number().int().nullable().optional(),
  groupPriceThresholds: z.array(groupPriceThresholdSchema).optional(),
  isDynamicPricing: z.boolean().optional(),
  dynamicPricing: dynamicPricingSchema.nullable().optional(),
} as const;

// `products.create` — superset of legacy CreateProductDto. `catalogId`,
// `contentCategory`, `contentSubcategory` are required (legacy @IsDefined);
// every other field is optional and stored verbatim. Pricing recompute is a
// pricing-track concern — `price` is accepted as opaque jsonb here.
export const createProductInput = z.object({
  ...productWritableFields,
  catalogId: z.uuid(),
  contentCategory: z.enum(categoryEnumValues),
  contentSubcategory: z.enum(subcategoryEnumValues),
});
export type CreateProductInput = z.infer<typeof createProductInput>;

// Widened patch — every column the legacy UpdateProductDto exposes (which is
// `PartialType(CreateProductDto)` minus catalogId). Allows the same shape as
// create except every field is optional, plus `description` / `stars` /
// content category overrides. Keeps the "at least one field present" refine
// so callers don't fire empty patches.
export const updateProductPatchSchemaWidened = z
  .object({
    ...productWritableFields,
    contentCategory: z.enum(categoryEnumValues).optional(),
    contentSubcategory: z.enum(subcategoryEnumValues).optional(),
    isHidden: z.boolean().optional(),
    isHiddenOnCms: z.boolean().optional(),
    isHiddenOnMiniPlanner: z.boolean().optional(),
    // Virtual field — merged into the `details` jsonb server-side (not a
    // column), so it doesn't clobber sibling `details` keys.
    hideTitle: z.boolean().optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: 'patch must contain at least one field to update',
  });
export type UpdateProductPatchWidened = z.infer<
  typeof updateProductPatchSchemaWidened
>;

// Wider update input — same `{ id, patch }` envelope but the widened patch.
// The narrow `updateProductInput` above stays for the (now-deprecated)
// description+stars-only path; both procedures share a tRPC name, the wider
// schema is the canonical one.
export const updateProductInputWidened = z.object({
  id: z.uuid(),
  patch: updateProductPatchSchemaWidened,
});
export type UpdateProductInputWidened = z.infer<
  typeof updateProductInputWidened
>;

// `products.remove` — soft delete. Identity only.
export const removeProductInput = z.object({ id: z.uuid() });
export type RemoveProductInput = z.infer<typeof removeProductInput>;

// `DuplicateProductOptionsDto` ported. Each boolean toggles which fields the
// copy inherits from the source. `catalogId` (when present) re-targets the
// duplicate to a different catalog.
export const duplicateProductOptionsSchema = z.object({
  catalogId: z.uuid().optional(),
  category: z.enum(categoryEnumValues).optional(),
  subcategory: z.enum(subcategoryEnumValues).optional(),
  locationAndTime: z.boolean().optional(),
  suppliers: z.boolean().optional(),
  titleAndDescription: z.boolean().optional(),
  attachments: z.boolean().optional(),
  videos: z.boolean().optional(),
  notes: z.boolean().optional(),
});
export type DuplicateProductOptions = z.infer<
  typeof duplicateProductOptionsSchema
>;

export const duplicateProductInput = z.object({
  id: z.uuid(),
  options: duplicateProductOptionsSchema,
});
export type DuplicateProductInput = z.infer<typeof duplicateProductInput>;

// `products.move` reuses the same options DTO server-side.
export const moveProductInput = duplicateProductInput;
export type MoveProductInput = DuplicateProductInput;

// Bulk: `productsBatch.bulkUpdate` — each item carries the same widened
// patch keys plus an `id`. Minimum one item.
export const bulkUpdateProductsItemSchema = z.object({
  id: z.uuid(),
  ...productWritableFields,
  contentCategory: z.enum(categoryEnumValues).optional(),
  contentSubcategory: z.enum(subcategoryEnumValues).optional(),
  isHidden: z.boolean().optional(),
  isHiddenOnCms: z.boolean().optional(),
  isHiddenOnMiniPlanner: z.boolean().optional(),
});
export type BulkUpdateProductsItem = z.infer<
  typeof bulkUpdateProductsItemSchema
>;

export const bulkUpdateProductsInput = z.object({
  items: z.array(bulkUpdateProductsItemSchema).min(1),
});
export type BulkUpdateProductsInput = z.infer<typeof bulkUpdateProductsInput>;

// Bulk-image: append-only `attachments` write for a single product.
export const bulkUpdateProductImagesInput = z.object({
  id: z.uuid(),
  images: z.array(productAttachmentSchema).min(1),
});
export type BulkUpdateProductImagesInput = z.infer<
  typeof bulkUpdateProductImagesInput
>;

// Batch hashtags / cms visibility — match legacy DTOs (`ArrayMinSize(1)`).
export const batchAddHashtagsInput = z.object({
  productIds: z.array(z.uuid()).min(1),
  hashtags: z.array(z.string().min(1)).min(1),
});
export type BatchAddHashtagsInput = z.infer<typeof batchAddHashtagsInput>;

export const batchUpdateCmsVisibilityInput = z.object({
  productIds: z.array(z.uuid()).min(1),
  isHiddenOnCms: z.boolean(),
});
export type BatchUpdateCmsVisibilityInput = z.infer<
  typeof batchUpdateCmsVisibilityInput
>;

export const batchUpdateMiniPlannerVisibilityInput = z.object({
  productIds: z.array(z.uuid()).min(1),
  isHiddenOnMiniPlanner: z.boolean(),
});
export type BatchUpdateMiniPlannerVisibilityInput = z.infer<
  typeof batchUpdateMiniPlannerVisibilityInput
>;

// Small envelope for bulk/batch endpoints — legacy SuccessResponseDto.
export const writeSuccessSchema = z.object({
  success: z.literal(true),
  count: z.number().int().nonnegative(),
});
export type WriteSuccess = z.infer<typeof writeSuccessSchema>;

// ─── XLSX export ───────────────────────────────────────────────────
// Mirrors operations.exportXlsx contract — buffered base64 workbook plus an
// `over-cap` short-circuit so the UI can prompt the user to narrow. The
// legacy endpoint (`GET /products/export/excel`) took catalogId + optional
// ref; we keep the same surface to start.

export const PRODUCTS_EXPORT_ROW_CAP = 10_000;

export const exportProductsInput = z.object({
  catalogId: z.uuid(),
  ref: z.string().optional(),
});
export type ExportProductsInput = z.infer<typeof exportProductsInput>;

export const exportProductsResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    filename: z.string(),
    base64: z.string(),
    rows: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('over-cap'),
    count: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
  }),
]);
export type ExportProductsResult = z.infer<typeof exportProductsResultSchema>;
